/**
 * ReleaseTrackingPage — Phase 4
 *
 * Phase 4 adds:
 *  - Tab navigation: "Releases" ↔ "Feature Adoption"
 *  - FeatureAdoptionView (API 8) as a full dedicated tab
 *  - ReleasesTable skeleton loading state
 *  - Complete UI/UX overhaul of header and layout
 *
 * All preceding phases retained:
 *  Phase 2 — LiveUsersChip, ReleaseStatsBar, cursor pagination
 *  Phase 3 — ReleaseDetailPanel, AddFeatureModal, ExecuteReleaseModal
 */
import React, { useState, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FormControl, FormSelect, Button } from "react-bootstrap";

// API modules
import { fetchReleases } from "./api/releasesApi";
import { fetchClientFeatures } from "./api/clientsApi";

// Components
import ReleaseLoadingScreen from "./components/ReleaseLoadingScreen";
import ReleasesTable from "./components/ReleasesTable";
import ReleaseTimeline from "./components/ReleaseTimeline";
import ClientFilterPopover from "./components/ClientFilterPopover";
import CreateReleaseModal from "./components/CreateReleaseModal";
import ReleaseStatsBar from "./components/ReleaseStatsBar";
import ReleaseDetailPanel from "./components/ReleaseDetailPanel";
import FeatureAdoptionView from "./components/FeatureAdoptionView";

import "./ReleaseTrackingPage.css";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "READY", label: "Ready" },
  { value: "RELEASED", label: "Released" },
];

const PAGE_SIZE = 20;
/** Safety cap: stop fetching after this many releases */
const MAX_RELEASES = 500;

const extractCursor = (result) =>
  result?.pagination?.nextCursor || result?.meta?.cursor || null;

/* ─────────────────────────────────────────────────────────────── */

const ReleaseTrackingPage = () => {
  const queryClient = useQueryClient();

  // ── Tab: "releases" | "adoption"
  const [activeTab, setActiveTab] = useState("releases");

  // ── Sub-view within "releases" tab: "list" | "detail" | "timeline"
  const [subView, setSubView] = useState("list");
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  // ── Table controls
  const [tableSearch, setTableSearch] = useState("");
  const [tableStatus, setTableStatus] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);

  // ── API 1: Releases — fetch ALL upfront (no pagination / Load more)
  const releasesQuery = useQuery({
    queryKey: ["releases"],
    queryFn: async () => {
      let all = [];
      let cursor;
      do {
        const res = await fetchReleases(PAGE_SIZE, cursor);
        const items = res?.data || [];
        all = [...all, ...items];
        cursor = extractCursor(res);
      } while (cursor && all.length < MAX_RELEASES);
      return { data: all };
    },
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  const allReleases = releasesQuery.data?.data ?? [];

  // ── API 7: Client timeline
  const clientFeaturesQuery = useQuery({
    queryKey: ["clientFeatures", selectedClient?.clientId],
    queryFn: () => fetchClientFeatures(selectedClient?.clientId, 50),
    enabled: subView === "timeline" && !!selectedClient?.clientId,
  });

  const refreshReleases = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["releases"] });
  }, [queryClient]);

  // ── Filtering
  const filteredReleases = useMemo(() => {
    const q = tableSearch.toLowerCase().trim();
    const stat = (tableStatus || "").toLowerCase();
    return allReleases.filter((row) => {
      const okStat = !stat || (row.status || "").toLowerCase() === stat;
      if (!okStat) return false;
      if (!q) return true;
      return (
        (row.releaseName || "").toLowerCase().includes(q) ||
        (row.status || "").toLowerCase().includes(q) ||
        (row.createdBy || "").toLowerCase().includes(q)
      );
    });
  }, [allReleases, tableSearch, tableStatus]);

  // ── Navigation
  const handleRowClick = (release) => {
    setSelectedRelease(release);
    setSubView("detail");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackFromDetail = () => {
    setSelectedRelease(null);
    setSubView("list");
  };

  const handleApplyClientFilter = ({ org, center, clientId }) => {
    setSelectedClient({ org, center, clientId });
    setSelectedRelease(null);
    setSubView("timeline");
  };

  const handleClearClientFilter = () => {
    setSelectedClient(null);
    setSubView("list");
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "releases") {
      setSubView("list");
      setSelectedRelease(null);
      setSelectedClient(null);
    }
  };

  // ── Derived states
  const isListLoading = subView === "list" && releasesQuery.isLoading;
  const isTimelineLoading = subView === "timeline" && clientFeaturesQuery.isLoading;
  const errorMessage =
    (subView === "list" && releasesQuery.error?.message) ||
    (subView === "timeline" && clientFeaturesQuery.error?.message) || "";

  // ── Render
  return (
    <div className="release-tracking-page">
      <div className={`release-tracking-page__card${activeTab === "adoption" ? " release-tracking-page__card--overflow-visible" : ""}`}>
      {/* ─── Page Header ───────────────────────────────────── */}
      <header className="release-tracking-page__header">
        <div className="release-tracking-page__header-row">
          <div className="release-tracking-page__header-left">
            <h1 className="release-tracking-page__title">
              <span className="release-tracking-page__title-icon" aria-hidden="true">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </span>
              Release Tracking
            </h1>
            <p className="release-tracking-page__tagline">
              Track feature releases across clients and timelines
            </p>
          </div>
          <div className="release-tracking-page__header-right">
          </div>
        </div>

        {/* Client chip — shown in timeline subview */}
        {subView === "timeline" && selectedClient && (
          <>
            <div className="release-tracking-page__toolbar" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="rtp-back-btn"
                onClick={handleClearClientFilter}
                aria-label="Back to releases"
                title="Back to releases"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                Back
              </button>
              <div className="release-tracking-page__toolbar-spacer" />
              <ClientFilterPopover onApply={handleApplyClientFilter} />
              <div className="release-tracking-page__chip">
                <span className="release-tracking-page__chip-icon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <span className="release-tracking-page__chip-label">
                  {selectedClient.org}
                  {selectedClient.center && selectedClient.center !== selectedClient.org && (
                    <>
                      <span className="release-tracking-page__chip-sep">·</span>
                      {selectedClient.center}
                    </>
                  )}
                </span>
                <button
                  type="button"
                  className="release-tracking-page__chip-clear"
                  onClick={handleClearClientFilter}
                  aria-label="Clear client filter"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            <p className="release-tracking-page__subtitle">
              Viewing feature timeline for{" "}
              <strong>
                {selectedClient.org}
                {selectedClient.center && selectedClient.center !== selectedClient.org
                  ? ` · ${selectedClient.center}`
                  : ""}
              </strong>
            </p>
          </>
        )}
      </header>

      {/* ─── Tab Navigation ─────────────────────────────────── */}
      <nav className="rtp-tabs" aria-label="Release Tracking sections" role="tablist">
        <button
          type="button"
          id="tab-releases"
          className={`rtp-tab ${activeTab === "releases" ? "rtp-tab--active" : ""}`}
          onClick={() => handleTabChange("releases")}
          role="tab"
          aria-selected={activeTab === "releases"}
          aria-controls="tabpanel-releases"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true">
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8M12 17v4" />
          </svg>
          All Releases
          {allReleases.length > 0 && (
            <span className="rtp-tab__badge">{allReleases.length}</span>
          )}
        </button>
        <button
          type="button"
          id="tab-adoption"
          className={`rtp-tab ${activeTab === "adoption" ? "rtp-tab--active" : ""}`}
          onClick={() => handleTabChange("adoption")}
          role="tab"
          aria-selected={activeTab === "adoption"}
          aria-controls="tabpanel-adoption"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          Feature Adoption
          <span className="rtp-tab__new">API 8</span>
        </button>
      </nav>

      {/* ─── Content well (tabs + panel) ────────────────────── */}
      <div className="release-tracking-page__content">
      {/* ─── Error banner ──────────────────────────────────── */}
      {errorMessage && (
        <div className="release-tracking-page__error" role="alert">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span><strong>Error:</strong> {errorMessage}</span>
        </div>
      )}

      {/* ─── Tab: Feature Adoption ──────────────────────────── */}
      <div
        role="tabpanel"
        id="tabpanel-adoption"
        aria-labelledby="tab-adoption"
        hidden={activeTab !== "adoption"}
      >
        {activeTab === "adoption" ? <FeatureAdoptionView /> : null}
      </div>

      {/* ─── Tab: Releases ─────────────────────────────────── */}
      <div
        role="tabpanel"
        id="tabpanel-releases"
        aria-labelledby="tab-releases"
        hidden={activeTab !== "releases"}
      >
      {activeTab === "releases" ? (
        <>
          {isTimelineLoading ? (
            <ReleaseLoadingScreen message="Loading client timeline..." />
          ) : subView === "detail" && selectedRelease ? (

            /* ── Release Detail Panel ──────────────────────── */
            <ReleaseDetailPanel
              release={selectedRelease}
              onBack={handleBackFromDetail}
              onReleaseUpdated={refreshReleases}
            />

          ) : subView === "timeline" ? (

            /* ── Client Timeline ───────────────────────────── */
            <ReleaseTimeline
              data={clientFeaturesQuery.data?.data || []}
              title="Release Timeline"
              description={`Features released to ${selectedClient?.org || "this client"}${selectedClient?.center && selectedClient.center !== selectedClient.org ? ` · ${selectedClient.center}` : ""}`}
            />

          ) : (

            /* ── Releases List ─────────────────────────────── */
            <>
              {/* Stats */}
              <ReleaseStatsBar
                releases={allReleases}
                activeStatus={tableStatus}
                onStatusClick={(status) => setTableStatus(status)}
              />

              {/* Toolbar */}
              <div className="release-tracking-page__table-toolbar">
                <FormControl
                  type="search"
                  placeholder="Search by name, status, created by…"
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  className="release-tracking-page__table-search"
                  size="sm"
                  aria-label="Search releases"
                />
                <FormSelect
                  value={tableStatus}
                  onChange={(e) => setTableStatus(e.target.value)}
                  className="release-tracking-page__table-status"
                  size="sm"
                  aria-label="Filter by status"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </FormSelect>
                <div className="release-tracking-page__table-toolbar-spacer" />
                <Button
                  variant="primary"
                  size="sm"
                  className="release-tracking-page__btn-create"
                  onClick={() => setShowCreateModal(true)}
                >
                  + Create Release
                </Button>
                <ClientFilterPopover onApply={handleApplyClientFilter} />
              </div>

              {/* Table: all filtered releases; skeleton only on initial/refetch, not on Load more */}
              <ReleasesTable
                data={filteredReleases}
                onRowClick={handleRowClick}
                loading={releasesQuery.isLoading || releasesQuery.isFetching}
              />

              {/* ── Footer: Showing X (all data loaded upfront) ─────────────── */}
              {!releasesQuery.isLoading && (
                <div className="release-tracking-page__pagination">
                  <span className="release-tracking-page__pagination-info" aria-live="polite">
                    Showing {filteredReleases.length} release{filteredReleases.length !== 1 ? "s" : ""}
                  </span>
                </div>
              )}

              {/* Create modal */}
              <CreateReleaseModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onSuccess={() => {
                  refreshReleases();
                  setShowCreateModal(false);
                }}
              />
            </>
          )}
        </>
      ) : null}
      </div>

      </div>
      </div>
    </div>
  );
};

export default ReleaseTrackingPage;
